import { Component, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { Product } from '../product';
import { ProductService } from '../product.service';
import { map, mergeMap, take, tap } from 'rxjs/operators';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { CanonicalService } from '../../core/canonical.service';
import { CategoryService } from '../../main-shared/categories-display/category.service';
import { Category } from '../../main-shared/categories-display/category';
import { environment } from '../../../environments/environment';
import { PagedProducts } from '../paged-products';

@Component({
  selector: 'app-list-products',
  templateUrl: './list-products.component.html',
  styleUrls: ['./list-products.component.scss'],
})
export class ListProductsComponent implements OnInit {
  products$: Observable<Product[]> | undefined;
  category = '';
  categoryTitle = '';
  isCategoryFiltered = false;
  page = 1;
  lastPage = 1;
  totalProducts = 0;
  productsPerPage = 16;

  constructor(
    readonly productService: ProductService,
    private readonly route: ActivatedRoute,
    private readonly categoryService: CategoryService,
    private title: Title,
    private meta: Meta,
    private router: Router,
    private readonly canonicalService: CanonicalService
  ) {}

  ngOnInit(): void {
    this.products$ = this.route.queryParamMap.pipe(
      mergeMap((params) =>
        params.has('category')
          ? this.productService
              .getAllPagedCategory(
                params?.get('category')!,
                this.getPageNumberFromParamMap(params),
                this.productsPerPage
              )
              .pipe(
                tap((res) =>
                  this.setPageFromResults(
                    res,
                    this.getPageNumberFromParamMap(params)
                  )
                ),
                map((res) => res.products),
                tap(() => (this.isCategoryFiltered = true)),
                tap(() => (this.category = params?.get('category')!)),
                tap(() => this.setCategoryTitle(params?.get('category')!))
              )
          : this.productService
              .getAllPaged(
                this.getPageNumberFromParamMap(params),
                this.productsPerPage
              )
              .pipe(
                tap((res) =>
                  this.setPageFromResults(
                    res,
                    this.getPageNumberFromParamMap(params)
                  )
                ),
                map((res) => res.products),
                tap(() => (this.isCategoryFiltered = false)),
                tap(() => this.setMetaAndTitleNoCategory())
              )
      )
    );
  }

  getProductsForPage(page: number): void {
    this.page = page;
  }

  private getPageNumberFromParamMap(params: ParamMap): number {
    return params.has('page') ? +params.get('page')! : 1;
  }

  private setPageFromResults(res: PagedProducts, page: number) {
    this.totalProducts = res.count;
    this.lastPage = Math.ceil(this.totalProducts / this.productsPerPage);
    if (page > this.lastPage && this.lastPage !== 0) {
      this.page = this.lastPage;
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { page: this.lastPage },
        queryParamsHandling: 'merge',
      });
    } else {
      this.page = page;
    }
  }

  private setCategoryTitle(categoryId: string): void {
    this.categoryService
      .getAllCached()
      .pipe(
        map((categories) =>
          this.setMetaAndTitleForCategory(categories, categoryId)
        ),
        take(1)
      )
      .subscribe();
  }

  private setMetaAndTitleForCategory(
    categories: Category[],
    categoryID: string
  ): void {
    const category = this.categoryService.getCategoryByIDFromCache(
      categories,
      categoryID
    );
    if (category) {
      const description = `Outils d'intervention pour ${category.name}`;
      const metaTags: MetaDefinition[] = [
        { name: 'description', content: description },
        { property: 'og:title', content: category.name },
        {
          property: 'og:url',
          content: environment.baseURL + this.router.routerState.snapshot.url,
        },
        { property: 'og:description', content: description },
      ];
      metaTags.forEach((m) => this.meta.updateTag(m));
      this.title.setTitle(category.name);
      this.categoryTitle = category.name;
      this.canonicalService.setCanonicalURL();
    } else {
      const description = `Tous les outils d'intervention`;
      const metaTags: MetaDefinition[] = [
        { name: 'description', content: description },
        { property: 'og:title', content: 'Outils' },
        {
          property: 'og:url',
          content: environment.baseURL + this.router.routerState.snapshot.url,
        },
        { property: 'og:description', content: description },
      ];
      metaTags.forEach((m) => this.meta.updateTag(m));
      this.canonicalService.setCanonicalURL();
    }
  }

  private setMetaAndTitleNoCategory() {
    const metaTags: MetaDefinition[] = [
      { name: 'description', content: `Outils d'intervention` },
      { property: 'og:title', content: `Outils` },
      {
        property: 'og:url',
        content: environment.baseURL + this.router.routerState.snapshot.url,
      },
      { property: 'og:description', content: `Outils d'intervention` },
    ];
    metaTags.forEach((m) => this.meta.updateTag(m));
    this.canonicalService.setCanonicalURL();
  }
}
